import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PlaybookSidebar } from './PlaybookSidebar';
import { ContractDetail } from './ContractDetail';
import { ConfirmationModal } from './ConfirmationModal';
import { ContractTypeModal, ContractTypeFormData, ContractTypeResult } from './ContractTypeModal';
import * as api from '../services/apiClient';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import type { ContractListItem, ContractDetailData, ClauseListItem } from '../shared/playbook-types';
import { logger } from '../utils/logger';
import '../styles/PlaybookCommon.css';

// Helper to clean framework filename: removes ID prefix if starts with digit and contains "_"
const cleanFrameworkName = (s3Key: string | null): string | null => {
  if (!s3Key) return null;
  const filename = s3Key.split('/').pop() || null;
  if (!filename) return null;
  // If starts with digit AND contains at least one "_", remove everything before first "_"
  if (/^\d/.test(filename) && filename.includes('_')) {
    return filename.substring(filename.indexOf('_') + 1);
  }
  return filename;
};

export const MesContratsPage: React.FC = () => {
  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [selectedContractDetail, setSelectedContractDetail] = useState<ContractDetailData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add/Edit modal state
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalMode, setContractModalMode] = useState<'add' | 'edit'>('add');
  const [editContractData, setEditContractData] = useState<ContractTypeFormData | null>(null);
  const [editContractId, setEditContractId] = useState<number | null>(null);
  const [isLoadingForEdit, setIsLoadingForEdit] = useState(false);

  // Available clauses for the modal
  const [allClauses, setAllClauses] = useState<ClauseListItem[]>([]);

  const latestDetailRequestRef = useRef<number>(0);
  const prevAliasRef = useRef<typeof activeAlias | undefined>(undefined);

  const { initialContractId, setInitialContractId } = useAppContext();
  const { authStatus } = useAuth();
  const { activeAlias } = useImpersonation();
  const isAdmin = authStatus?.user?.is_admin === true;

  // Handle add request - opens modal in add mode
  const handleAddRequest = useCallback(() => {
    setContractModalMode('add');
    setEditContractData(null);
    setEditContractId(null);
    setContractModalOpen(true);
  }, []);

  // Handle edit request - opens modal in edit mode with current data
  const handleEditRequest = useCallback(async (id: number, name: string) => {
    setContractModalMode('edit');
    setEditContractId(id);

    // Check if we already have the detail for this specific contract
    if (selectedContractId === id && selectedContractDetail) {
      // Use already loaded data
      const s3Key = selectedContractDetail.framework_s3_key || null;
      const frameworkName = cleanFrameworkName(s3Key);
      setEditContractData({
        name: selectedContractDetail.name,
        directives: selectedContractDetail.note || '',
        clauses: selectedContractDetail.clause_types?.map(c => ({ id: c.id, name: c.name })) || [],
        frameworkS3Key: s3Key,
        frameworkFileName: frameworkName,
      });
      setContractModalOpen(true);
    } else {
      // Need to fetch the detail first
      setIsLoadingForEdit(true);
      try {
        const response = await api.getContractDetail(id);
        if (response.success && response.data) {
          const s3Key = response.data.framework_s3_key || null;
          const frameworkName = cleanFrameworkName(s3Key);
          setEditContractData({
            name: response.data.name,
            directives: response.data.note || '',
            clauses: response.data.clause_types?.map(c => ({ id: c.id, name: c.name })) || [],
            frameworkS3Key: s3Key,
            frameworkFileName: frameworkName,
          });
          setContractModalOpen(true);
        } else {
          logger.error('Erreur lors du chargement des détails pour édition:', response.error);
          alert(response.error || 'Erreur lors du chargement des détails');
        }
      } catch (err) {
        logger.error('Erreur lors du chargement des détails pour édition:', err);
        alert('Erreur lors du chargement des détails');
      } finally {
        setIsLoadingForEdit(false);
      }
    }
  }, [selectedContractId, selectedContractDetail]);

  // Handle contract modal success
  const handleContractModalSuccess = useCallback((result: ContractTypeResult) => {
    // Update the list immediately
    setContracts(prev => {
      const existingIndex = prev.findIndex(c => c.id === result.id);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { id: result.id, name: result.name };
        return updated;
      } else {
        // Add new at the end
        return [...prev, { id: result.id, name: result.name }];
      }
    });

    // If we edited the currently selected contract, update its detail too
    if (selectedContractId === result.id) {
      setSelectedContractDetail(prev => prev ? {
        ...prev,
        name: result.name,
        note: result.note,
        clause_types: result.clause_types.map((c, index) => {
          // Preserve existing versions if available
          const existingClause = prev.clause_types.find(ec => ec.id === c.id);
          return {
            id: c.id,
            name: c.name,
            order: c.order ?? index + 1,
            versions: existingClause?.versions ?? [],
          };
        }),
      } : null);
    }

    // If it's a new contract, select it
    if (contractModalMode === 'add') {
      setSelectedContractId(result.id);
    }
  }, [selectedContractId, contractModalMode]);

  // Handle delete request (opens modal)
  const handleDeleteRequest = useCallback((id: number, name: string) => {
    setItemToDelete({ id, name });
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await api.deleteContract(itemToDelete.id);

      if (response.success) {
        // Remove from list
        setContracts(prev => prev.filter(c => c.id !== itemToDelete.id));

        // Deselect if it was the selected item
        if (selectedContractId === itemToDelete.id) {
          setSelectedContractId(null);
          setSelectedContractDetail(null);
        }

        console.log(`Contrat supprimé: ${itemToDelete.id} (${itemToDelete.name})`);
      } else {
        console.error('Erreur lors de la suppression:', response.error);
        alert(response.error || 'Erreur lors de la suppression du contrat');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert('Erreur lors de la suppression du contrat');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, selectedContractId, isDeleting]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  }, []);

  useEffect(() => {
    if (initialContractId) {
      setSelectedContractId(initialContractId);
      setInitialContractId(null);
    }
  }, [initialContractId, setInitialContractId]);

  // Reset selection when impersonation changes (skip initial mount)
  useEffect(() => {
    // Only reset if this is an actual alias change, not initial mount
    if (prevAliasRef.current !== undefined && prevAliasRef.current !== activeAlias) {
      setSelectedContractId(null);
      setSelectedContractDetail(null);
    }
    prevAliasRef.current = activeAlias;
  }, [activeAlias]);

  const fetchContracts = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const response = await api.getContracts();
      if (response.success && response.data) {
        setContracts(response.data);
      } else {
        setError(response.error || 'Échec du chargement des contrats');
        console.error('Error fetching contracts list:', response.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      console.error('Error fetching contracts list:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [activeAlias]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Fetch all clauses for the modal dropdown
  const fetchAllClauses = useCallback(async () => {
    try {
      const response = await api.getClauses();
      if (response.success && response.data) {
        setAllClauses(response.data);
      }
    } catch (err) {
      console.error('Error fetching clauses for modal:', err);
    }
  }, [activeAlias]);

  useEffect(() => {
    fetchAllClauses();
  }, [fetchAllClauses]);

  useEffect(() => {
    if (!selectedContractId) {
      setSelectedContractDetail(null);
      setDetailError(null);
      return;
    }

    const requestId = ++latestDetailRequestRef.current;

    const fetchDetail = async () => {
      setIsLoadingDetail(true);
      setDetailError(null);
      try {
        const response = await api.getContractDetail(selectedContractId);

        if (requestId !== latestDetailRequestRef.current) {
          return;
        }

        if (response.success && response.data) {
          setSelectedContractDetail(response.data as ContractDetailData);
        } else {
          console.error(`Error fetching contract detail ${selectedContractId}:`, response.error);
          setSelectedContractDetail(null);
          setDetailError(response.error || 'Échec du chargement des détails');
        }
      } catch (err) {
        if (requestId !== latestDetailRequestRef.current) {
          return;
        }

        const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
        console.error(`Error fetching contract detail ${selectedContractId}:`, err);
        setSelectedContractDetail(null);
        setDetailError(errorMessage);
      } finally {
        if (requestId === latestDetailRequestRef.current) {
          setIsLoadingDetail(false);
        }
      }
    };

    fetchDetail();
  }, [selectedContractId]);

  return (
    <div className="playbook-page">
      <div className="playbook-container">
        {error ? (
          <div className="playbook-sidebar">
            <div className="playbook-sidebar-header">
              <h2>Mes contrats</h2>
            </div>
            <div className="playbook-error-state">
              <p>{error}</p>
              <button onClick={fetchContracts} className="retry-button">
                Réessayer
              </button>
            </div>
          </div>
        ) : (
          <PlaybookSidebar
            title="Mes contrats"
            items={contracts}
            selectedId={selectedContractId}
            onSelectItem={setSelectedContractId}
            isLoading={isLoadingList}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Rechercher un contrat"
            emptyMessage="Aucun contrat trouvé"
            onAdd={isAdmin ? handleAddRequest : undefined}
            onEditItem={isAdmin ? handleEditRequest : undefined}
            onDeleteItem={isAdmin ? handleDeleteRequest : undefined}
          />
        )}

        <div className="playbook-main-content">
          <ContractDetail
            contract={selectedContractDetail}
            isLoading={isLoadingDetail}
            error={detailError}
            onRetry={() => {
              if (selectedContractId) {
                setDetailError(null);
                const id = selectedContractId;
                setSelectedContractId(null);
                setTimeout(() => setSelectedContractId(id), 0);
              }
            }}
            onEdit={isAdmin ? handleEditRequest : undefined}
            onDelete={isAdmin ? handleDeleteRequest : undefined}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Supprimer ce contrat ?"
        message="Cette action est irréversible. Le contrat sera définitivement supprimé."
        isLoading={isDeleting}
      />

      <ContractTypeModal
        isOpen={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        mode={contractModalMode}
        initialData={editContractData || undefined}
        contractId={editContractId || undefined}
        availableClauses={allClauses}
        onSubmitSuccess={handleContractModalSuccess}
        onClausesRefreshNeeded={fetchAllClauses}
      />
    </div>
  );
};
